import React from "react";
import {
    Box,
    Button,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    Heading,
    HStack,
    List,
    ListIcon,
    ListItem,
    Text,
    VStack,
} from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import { brandConfig } from "../../../brandConfig";

interface SubscriptionPlan {
    id: string;
    name: string;
    tier: string;
    amount: number;
    interval: string;
    description: string;
    features: string[];
    color?: string;
    badge?: string;
    ctaText?: string;
    isRecommended?: boolean;
}

interface SubscriptionPlanCardProps {
    plan: SubscriptionPlan;
    isCurrentPlan: boolean;
    isSelecting: boolean;
    onSelect: (plan: SubscriptionPlan) => void;
}

export const SubscriptionPlanCard: React.FC<SubscriptionPlanCardProps> = ({
    plan,
    isCurrentPlan,
    isSelecting,
    onSelect,
}) => {
    const cardBg = brandConfig.colors.background.cardGradient;
    const borderColor = brandConfig.colors.border.darkCard;
    const textPrimary = brandConfig.colors.text.inverse;
    const textSecondary = brandConfig.colors.text.secondaryDark;
    const textMuted = brandConfig.colors.text.mutedDark;

    const planColor = plan.color || "blue";
    const displayPrice = `$${plan.amount}`;
    const displayInterval =
        plan.interval === "month"
            ? "per month"
            : plan.interval === "year"
            ? "per year"
            : `per ${plan.interval}`;

    return (
        <Card
            position="relative"
            overflow="hidden"
            borderWidth={isCurrentPlan ? "2px" : plan.isRecommended ? "2px" : "1px"}
            borderColor={
                isCurrentPlan
                    ? "green.400"
                    : plan.isRecommended
                    ? brandConfig.colors.primary
                    : borderColor
            }
            bg={cardBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            _hover={{
                transform: "translateY(-4px)",
                boxShadow: "0 12px 40px 0 rgba(0, 0, 0, 0.5)",
                transition: "all 0.2s",
            }}
        >
            {(isCurrentPlan || plan.badge) && (
                <Box
                    position="absolute"
                    top="0"
                    right="0"
                    bg={
                        isCurrentPlan
                            ? "green.500"
                            : plan.isRecommended
                            ? brandConfig.colors.primary
                            : `${planColor}.500`
                    }
                    color="white"
                    px={3}
                    py={1}
                    fontSize="xs"
                    fontWeight="bold"
                    borderBottomLeftRadius="md"
                >
                    {isCurrentPlan ? "Current Plan" : plan.badge}
                </Box>
            )}

            <CardHeader pb={3}>
                <VStack align="start" spacing={2}>
                    <Heading size="md" color={textPrimary}>
                        {plan.name}
                    </Heading>
                    <HStack>
                        <Text fontSize="2xl" fontWeight="bold" color={textPrimary}>
                            {displayPrice}
                        </Text>
                        <Text fontSize="sm" color={textMuted}>
                            {displayInterval}
                        </Text>
                    </HStack>
                    <Text fontSize="xs" color={textSecondary}>
                        {plan.description}
                    </Text>
                </VStack>
            </CardHeader>

            <CardBody py={4}>
                <List spacing={2}>
                    {plan.features.slice(0, 6).map((feature: string, idx: number) => (
                        <ListItem key={idx} fontSize="xs">
                            <HStack align="start" spacing={1}>
                                <ListIcon
                                    as={CheckIcon}
                                    color={`${planColor}.500`}
                                    mt={0.5}
                                    minW="16px"
                                />
                                <Text color={textPrimary}>{feature}</Text>
                            </HStack>
                        </ListItem>
                    ))}
                    {plan.features.length > 6 && (
                        <Text fontSize="xs" color={textMuted} pl={6}>
                            +{plan.features.length - 6} more features
                        </Text>
                    )}
                </List>
            </CardBody>

            <CardFooter>
                {isCurrentPlan ? (
                    <Button
                        w="100%"
                        size="sm"
                        variant="outline"
                        borderColor="green.400"
                        color="green.400"
                        isDisabled
                    >
                        Current Plan
                    </Button>
                ) : (
                    <Button
                        w="100%"
                        size="sm"
                        bg={plan.isRecommended ? brandConfig.colors.primary : "transparent"}
                        color={plan.isRecommended ? "white" : textPrimary}
                        borderWidth={plan.isRecommended ? "0" : "1px"}
                        borderColor={borderColor}
                        _hover={{
                            bg: plan.isRecommended
                                ? brandConfig.colors.primaryHover
                                : brandConfig.colors.background.main,
                            transform: "scale(1.02)",
                        }}
                        isLoading={isSelecting}
                        onClick={() => onSelect(plan)}
                    >
                        {plan.ctaText || "Select Plan"}
                    </Button>
                )}
            </CardFooter>
        </Card>
    );
};

export type { SubscriptionPlan };
